import React, { useEffect, useState, useCallback } from "react";
import { Loader2, Database, HardDrive, Cpu, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api } from "@/lib/api";

const mb = (v) => (v === null || v === undefined ? "—" : `${v} MB`);

const ReportCard = ({ r, title, testid }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm space-y-1" data-testid={testid}>
    <p className="font-semibold text-slate-800">{title} <span className="text-xs text-slate-400">{(r.at || "").slice(0, 16).replace("T", " ")}</span></p>
    <p className="flex items-center gap-2"><Database className="h-4 w-4 text-blue-800" /> Database: data {mb(r.database?.data_mb)} · on disk {mb(r.database?.storage_mb)} · indexes {mb(r.database?.index_mb)}</p>
    <p className={`pl-6 ${r.images_inside_database?.payments ? "text-red-700" : "text-green-700"}`}>Images still inside the database: {r.images_inside_database?.payments} payments · {mb(r.images_inside_database?.mb)}</p>
    <p className="flex items-center gap-2"><HardDrive className="h-4 w-4 text-teal-700" /> File storage (payments): {Object.entries(r.file_storage_payment_files || {}).map(([k, v]) => `${k}: ${v.files} files, ${v.mb} MB`).join(" · ") || "none yet"}</p>
    <p className="pl-6 text-xs text-slate-500">Other files (count): student photos {r.file_storage_other_counts?.student_photos} · site uploads {r.file_storage_other_counts?.site_uploads} · diary {r.file_storage_other_counts?.diary_photos} · message files {r.file_storage_other_counts?.message_attachments}</p>
    <p className="flex items-center gap-2"><Cpu className="h-4 w-4 text-amber-700" /> RAM: app {mb(r.ram?.process_mb)} · server {mb(r.ram?.container_used_mb)} of {mb(r.ram?.container_limit_mb)}</p>
  </div>
);

const STEPS = [
  { kind: "backup", label: "1. Create verified backup", help: "Saves every payment, screenshot, receipt, ledger, correction and audit record to file storage in small parts; each part is re-downloaded and checked." },
  { kind: "copy", label: "2. Copy proofs & receipts to file storage", help: "5 payments at a time. Each image is uploaded once (identical screenshots share one file) and re-checked. Database images are NOT removed." },
  { kind: "verify", label: "3. Verify all payments & proof links", help: "Checks every payment, receipt and sibling payment opens from file storage and matches the database image exactly." },
  { kind: "remove", label: "4. Replace database copies with links", help: "Only allowed after step 3 shows 0 broken and 0 not copied. You start it yourself; files are never deleted." },
];

const JobLine = ({ j }) => (
  <div className="text-xs rounded-lg bg-slate-50 border border-slate-100 p-2" data-testid={`storage-job-${j.kind}`}>
    <b>{j.kind}</b> · {j.status === "running" ? "paused — press Continue" : j.status} · {j.done}/{j.total} {j.error && <span className="text-red-700">· {j.error}</span>}
    {j.result && <p className="text-[11px] text-slate-600 mt-1">{Object.entries(j.result).filter(([, v]) => typeof v !== "object").map(([k, v]) => `${k.replace(/_/g, " ")}: ${v}`).join(" · ")}</p>}
    {j.result?.broken?.length > 0 && <p className="text-red-700">Broken: {j.result.broken.join(" | ")}</p>}
    {(j.errors || []).length > 0 && <p className="text-red-700">{j.errors.slice(0, 5).join(" | ")}</p>}
  </div>
);

export default function StoragePanel() {
  const [r, setR] = useState(null);
  const [snaps, setSnaps] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [bks, setBks] = useState([]);
  const [busy, setBusy] = useState("");
  const load = useCallback(async () => {
    const [a, b, c, d] = await Promise.all([api.get("/admin/storage/report"), api.get("/admin/storage/snapshots"),
      api.get("/admin/storage/jobs"), api.get("/admin/storage/backups")]);
    setR(a.data); setSnaps(b.data.items); setJobs(c.data.items); setBks(d.data.items);
  }, []);
  useEffect(() => { load().catch(() => toast.error("Could not load storage report")); }, [load]);
  const open = jobs.find((j) => j.status === "running");
  const [live, setLive] = useState(null);
  const run = async (key, fn) => { setBusy(key); try { await fn(); await load(); } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); } finally { setBusy(""); } };
  const drive = async (kind) => {
    setBusy(kind);
    try {
      let { data: j } = await api.post(`/admin/storage/start/${kind}`);
      setLive(j);
      let fails = 0;
      while (j.status === "running") {
        try { ({ data: j } = await api.post(`/admin/storage/jobs/${j.id}/step`)); setLive(j); fails = 0; }
        catch (e) {
          if (e.response?.status === 500 || e.response?.status === 404) throw e;
          fails += 1; if (fails > 8) throw e;
          await new Promise((r) => setTimeout(r, 4000));
        }
      }
      toast.success(`${kind} finished`);
    } catch (e) { toast.error(e.response?.data?.detail || "Stopped — press Continue to resume"); }
    finally { setBusy(""); setLive(null); load().catch(() => {}); }
  };
  const dl = async (bid, n) => {
    const { data } = await api.get(`/admin/storage/backups/${bid}/parts/${n}`, { responseType: "blob" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(data); a.download = `ptg-backup-${bid.slice(0, 8)}-part${n}.jsonl.gz`; a.click();
  };
  if (!r) return <Loader2 className="h-5 w-5 animate-spin" />;
  return (
    <div className="space-y-4" data-testid="storage-panel">
      <ReportCard r={r} title="Now" testid="storage-now" />
      <div className="flex gap-2 flex-wrap">
        {["before", "after"].map((l) => <Button key={l} size="sm" variant="outline" disabled={!!busy} onClick={() => run(l, () => api.post("/admin/storage/report/snapshot", { label: l }))} data-testid={`storage-snap-${l}`}>Save “{l}” report</Button>)}
      </div>
      {snaps.length > 0 && <div className="grid md:grid-cols-2 gap-3">{snaps.slice(0, 4).map((s) => <ReportCard key={s.id} r={s.report} title={`Saved: ${s.label}`} testid={`storage-snap-card-${s.id}`} />)}</div>}
      <div className="space-y-2">
        {STEPS.map((s) => (
          <div key={s.kind} className="rounded-xl border border-slate-200 bg-white p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div><p className="font-semibold text-slate-800 text-sm">{s.label}</p><p className="text-xs text-slate-500">{s.help}</p></div>
            <Button size="sm" disabled={!!busy || (open && open.kind !== s.kind)} onClick={() => { if (s.kind === "remove" && !window.confirm("Replace database images with secure file links? Files are kept, nothing is deleted. Continue?")) return; drive(s.kind); }} className="bg-blue-900 hover:bg-blue-950 shrink-0" data-testid={`storage-step-${s.kind}`}>
              {busy === s.kind ? <><Loader2 className="h-4 w-4 animate-spin mr-1" />{live ? `${live.done}/${live.total}` : ""}</> : open?.kind === s.kind ? "Continue" : "Run"}</Button>
          </div>))}
      </div>
      {bks.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-3 text-xs space-y-1" data-testid="storage-backups">
          <p className="font-semibold text-sm text-slate-800">Backups</p>
          {bks.map((b) => (
            <div key={b.id} className="flex flex-wrap items-center gap-2">
              <span>{(b.created_at || "").slice(0, 16).replace("T", " ")} · {b.size_mb} MB · {b.verified ? "✅ verified" : "❌ not verified"} · {b.payments_checked} payments</span>
              {b.parts.map((p, i) => <Button key={i} size="sm" variant="outline" onClick={() => dl(b.id, i + 1)} data-testid={`storage-backup-dl-${b.id}-${i + 1}`}><Download className="h-3 w-3 mr-1" />Part {i + 1}</Button>)}
            </div>))}
        </div>)}
      {busy && live && <p className="text-xs text-blue-800" data-testid="storage-live-progress">Working on {live.kind}: {live.done} of {live.total} — keep this page open. If you close it, press Continue later; nothing is lost.</p>}
      <div className="space-y-1">{jobs.slice(0, 6).map((j) => <JobLine key={j.id} j={busy && live?.id === j.id ? { ...j, ...live, status: "working" } : j} />)}</div>
    </div>
  );
}
